import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { GIButton } from 'app-components';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl/dist';

import { ROOT_STATE } from '../../../store/store.types';
import { ctaHandlers } from '../../../utils/dashboard/stateSpecificUtils';
import {
    ALL_ELIGIBILITY_TYPES,
    DMI_DOCUMENT_UPLOAD_STATUS,
    VERIFICATION_STATUSES,
} from '../../../constants/individualPortal/dashboard/dashboardModelTypes';
import Tooltip from '../../../components/common/tooltip/Tooltip';
import states from '../../../constants/configs/states';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import { IneligibilityReasons } from './IneligibilityReasons';
import {
    IProcessedApplicant,
    IuseEligibilityDetailsContainerProps,
} from '../../../constants/individualPortal/eligibility/eligibility.types';

const StyledTitle = styled.div`
    color: ${({ theme }) => theme.gray2};
    font-weight: 600;
    margin-bottom: 12px;
`;

export const useEligibilityDetailsContainer = (props: IuseEligibilityDetailsContainerProps) => {
    const { showActionNeeded, stateCode } = props;
    const eligibilityResults = useSelector((state: ROOT_STATE) => state.individualPortal.eligibilityResults);
    const displayEligibilityFactors = useSelector(
        (state: ROOT_STATE) => state.common.environment.flags.displayEligibilityFactors,
    );
    const environment = useSelector((state: ROOT_STATE) => state.common.environment);
    const {
        language,
        flags: { isQleMultipleDocumentSupport },
    } = environment;
    const { remainingDaysForROP, caseNumber, isLatestApplication } = eligibilityResults;
    const history = useHistory();

    /**
     *  Returns type of action needed for Eligibility Details section (3d column).
     * @param exchangeEligible
     * @param assessedMedicaidMAGIEligible
     * @param assessedChipEligible
     * @param verificationStatus
     * @param caseNumber
     * @param isLatestApplication
     * @returns {*}
     */
    const getActionNeeded = (
        exchangeEligible: boolean,
        assessedMedicaidMAGIEligible: boolean,
        assessedChipEligible: boolean,
        verificationStatus: string,
        dmiDocumentUploadstatus: string,
        caseNumber: string,
        isLatestApplication: boolean,
    ) => {
        if (!isLatestApplication || !showActionNeeded) return false;

        if (
            verificationStatus === VERIFICATION_STATUSES.ACTION_NEEDED &&
            dmiDocumentUploadstatus === DMI_DOCUMENT_UPLOAD_STATUS.PENDING &&
            exchangeEligible
        ) {
            return (
                <>
                    <StyledTitle>
                        <FormattedMessage id="eligibility_results.details.table_header3" />
                    </StyledTitle>
                    <GIButton
                        id="eligibilityDocsRequired"
                        onClickHandler={() => ctaHandlers.goToUploadDocs(history, caseNumber, language, isQleMultipleDocumentSupport)}
                        addlClassNames="usa-button usa-button--outline gi-button gi-button__outline eligibility-details__action--btn"
                        labelId="button.documents_required"
                        data-testid="eligibilityDocsRequired-button"
                    />
                    <p className="eligibility-details__action--note">
                        <FormattedMessage
                            id="eligibility_results.details.action_note1"
                            values={{ days: remainingDaysForROP }}
                        />
                    </p>
                </>
            );
        } else if (assessedMedicaidMAGIEligible || assessedChipEligible) {
            return (
                <p className="eligibility-details__action--note">
                    <FormattedMessage id="eligibility_results.details.action_note3" />
                </p>
            );
        } else if (
            verificationStatus === VERIFICATION_STATUSES.ACTION_NEEDED &&
            dmiDocumentUploadstatus === DMI_DOCUMENT_UPLOAD_STATUS.PROCESSING
        ) {
            return (
                <p className="eligibility-details__action--note">
                    <FormattedMessage id="eligibility_results.details.action_note2" />
                </p>
            );
        }
    };

    const renderEligibilityDetailsData = (
        type: React.Key | null | undefined,
        index: string | undefined,
        {
            assessedChipOrMedicaidEligible,
            aptcAndCsrNotEligible,
            chipOrMedicaidMAGIEligible,
        }: {
            assessedChipOrMedicaidEligible: boolean;
            chipOrMedicaidMAGIEligible: boolean;
            aptcAndCsrNotEligible: boolean;
        },
    ) => {
        let result: ReactElement = <></>;

        switch (type) {
            case ALL_ELIGIBILITY_TYPES.QHP:
                result = <FormattedMessage id="eligibility_results.details.market_health_dental" />;
                break;
            case ALL_ELIGIBILITY_TYPES.APTC:
                result = (
                    <Tooltip
                        id="household_section.eligibility.status.eligible_aptc"
                        content="household_section.tooltip_aptc"
                        index={index}
                    />
                );
                break;

            case ALL_ELIGIBILITY_TYPES.STATE_SUBSIDY:
                result = (
                    <Tooltip
                        id="household_section.eligibility.status.eligible_state_subsidy"
                        content="household_section.tooltip_state_subsidy"
                        index={index}
                        dataAutomationId={`state_subsidy_${index}`}
                    />
                );
                break;
            case ALL_ELIGIBILITY_TYPES.CSR:
                result = (
                    <Tooltip
                        id="household_section.eligibility.status.csr"
                        content="household_section.tooltip_csr"
                        index={index}
                    />
                );
                break;
            case ALL_ELIGIBILITY_TYPES.ASSESSED_MAGI_MEDICAID:
                result = <FormattedMessage id="household_section.eligibility.indicator.assessed_medicaid" />;
                break;
            case ALL_ELIGIBILITY_TYPES.MEDICAID_REFERRAL:
                result = (
                    <Tooltip
                        id="eligibility_results.details.medicaid_referral"
                        content="household_section.tooltip_medicaid_referral"
                        index={index}
                    />
                );
                break;
            case ALL_ELIGIBILITY_TYPES.ASSESSED_CHIP:
                result = <FormattedMessage id="eligibility_results.details.assessed_chip" />;
                break;
            case ALL_ELIGIBILITY_TYPES.MEDICAID_OR_CHIP:
                result = <FormattedMessage id="eligibility_results.details.medicaid_or_chip" />;
                break;
            case ALL_ELIGIBILITY_TYPES.NOT_SEEKING:
                result = (
                    <Tooltip
                        id="household_section.eligibility.indicator.not_seeking_coverage"
                        content="household_section.tooltip_not_seeking"
                        index={index}
                    />
                );
                break;
            case ALL_ELIGIBILITY_TYPES.NOT_QHP: {
                if (stateCode === 'NV' && assessedChipOrMedicaidEligible) {
                    result = (
                        <FormattedMessage id="eligibility_results.details.not_market_health_dental.assessed_chip_or_medicaid" />
                    );
                } else {
                    const messageId =
                        stateCode === states.VIRGINIA &&
                        (assessedChipOrMedicaidEligible || chipOrMedicaidMAGIEligible) &&
                        aptcAndCsrNotEligible
                            ? 'eligibility_results.details.not_market_health_dental.potential_medicaid'
                            : 'eligibility_results.details.not_market_health_dental';
                    result = (
                        <FormattedMessage
                            id={messageId}
                            values={{
                                notEligibleTooltip: (
                                    <Tooltip
                                        id="common.not_eligible"
                                        content="household_section.tooltip_not_exchange_eligible"
                                        index={index}
                                    />
                                ),
                            }}
                        />
                    );
                }
                break;
            }
            case ALL_ELIGIBILITY_TYPES.POTENTIALLY_EMERGENCY_MEDICAID_ELIGIBLE:
                result = <FormattedMessage id="eligibility_results.details.potentially_emergency_medicaid_eligible" />;
                break;
            default:
                return result;
        }

        return result;
    };

    const getEligibilities = (
        member: IProcessedApplicant,
        assessedChipOrMedicaidEligible: boolean,
        chipOrMedicaidMAGIEligible: boolean,
        aptcAndCsrNotEligible: boolean,
        ineligibilityReasons: string[],
    ) => {
        const isIneligibilityFactorsVisible = displayEligibilityFactors && ineligibilityReasons?.length > 0;
        return (
            <>
                <StyledTitle>
                    <FormattedMessage id="eligibility_results.details.table_header2" />
                </StyledTitle>
                <ul className="eligibility-details__list">
                    {member.eligibilities.map((eligibility: React.Key | null | undefined) => {
                        const dataToRender = renderEligibilityDetailsData(eligibility, member.guid, {
                            assessedChipOrMedicaidEligible,
                            chipOrMedicaidMAGIEligible,
                            aptcAndCsrNotEligible,
                        });
                        const isEligibilityListItemVisible = dataToRender && dataToRender.props?.id;

                        return (
                            <ConditionalRendering isTrue={isEligibilityListItemVisible}>
                                <li key={eligibility}>{dataToRender}</li>
                            </ConditionalRendering>
                        );
                    })}
                </ul>
                <ConditionalRendering isTrue={isIneligibilityFactorsVisible}>
                    <hr />
                    <IneligibilityReasons ineligibilityReasons={ineligibilityReasons} />
                </ConditionalRendering>
            </>
        );
    };

    const getEligibilityDetails = (data: IProcessedApplicant[]) => {
        if (!data) return false;

        const ELIGIBILITY_DETAILS_HEADERS = [
            { id: 'name', text: '' },
            {
                id: 'eligibilities',
                text: '',
            },
        ];

        showActionNeeded &&
            ELIGIBILITY_DETAILS_HEADERS.push({
                id: 'action',
                text: '',
            });

        const eligibilityDetails = {
            columns: ELIGIBILITY_DETAILS_HEADERS,
            data: {},
        };

        eligibilityDetails.data = data.map((member: IProcessedApplicant) => {
            const {
                exchangeEligible,
                assessedMedicaidMAGIEligible,
                assessedChipEligible,
                verificationStatus,
                dmiDocumentUploadstatus,
                aptcEligible,
                csrEligible,
                chipEligible,
                medicaidMAGIEligible,
                ineligibilityReasons,
                taxHouseholdGroup,
            } = member && member.rawData;

            const assessedChipOrMedicaidEligible = assessedMedicaidMAGIEligible || assessedChipEligible;
            const chipOrMedicaidMAGIEligible = chipEligible || medicaidMAGIEligible;
            const aptcAndCsrNotEligible = !(aptcEligible || csrEligible);
            return {
                ...member,
                isCustomRender: taxHouseholdGroup !== 'NON_ITF' && member.rawData.seekingCoverage,
                eligibilities: getEligibilities(
                    member,
                    assessedChipOrMedicaidEligible,
                    chipOrMedicaidMAGIEligible,
                    aptcAndCsrNotEligible,
                    ineligibilityReasons,
                ),
                action: getActionNeeded(
                    exchangeEligible,
                    assessedMedicaidMAGIEligible,
                    assessedChipEligible,
                    verificationStatus,
                    dmiDocumentUploadstatus,
                    caseNumber,
                    isLatestApplication,
                ),
                customRenderMessage: (
                    <FormattedMessage
                        id="eligibility_results.details.itf_message"
                        values={{
                            member: member.name,
                            strong: function makeStrong(chunks) {
                                return <strong>{chunks}</strong>;
                            },
                        }}
                    />
                ),
            };
        });

        return eligibilityDetails;
    };

    return { getEligibilityDetails };
};
